import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { CheckCircle, XCircle, AlertTriangle, Plug, X } from "lucide-react";
import { WiredConexao, WiredTesteConexaoResposta } from "../../../services/wired";

interface WiredTesteConexaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  conexao: WiredConexao | null;
  resultado: WiredTesteConexaoResposta | null;
}

export function WiredTesteConexaoModal({ isOpen, onClose, conexao, resultado }: WiredTesteConexaoModalProps) {
  if (!isOpen || !resultado) return null;

  const funcionalidadesTeste = resultado.funcionalidades ?? [];
  const variaveis = resultado.quantidade_variaveis ?? { usuario: 0, mobilia: 0, global: 0 };
  const ausentes = resultado.ok ? funcionalidadesTeste.filter((f) => !f.variavel_existe) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Teste de Conexão
            </CardTitle>
            <CardDescription>
              {conexao ? `${conexao.nome} (quarto ${conexao.room_id})` : ""}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {resultado.ok ? (
            <>
              <div className="p-3 bg-green-50 dark:bg-green-950/40 rounded-lg border border-transparent dark:border-green-800">
                <div className="flex items-center gap-2 text-sm font-medium text-green-900 dark:text-green-300">
                  <CheckCircle className="h-4 w-4" />
                  Conexão estabelecida com sucesso
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                {(
                  [
                    ["Usuário", variaveis.usuario],
                    ["Mobília", variaveis.mobilia],
                    ["Global", variaveis.global],
                  ] as const
                ).map(([label, valor]) => (
                  <div key={label} className="rounded-lg border border-border p-3">
                    <div className="text-xl font-bold text-foreground">{valor}</div>
                    <div className="text-xs text-muted-foreground">Variáveis de {label.toLowerCase()}</div>
                  </div>
                ))}
              </div>

              {ausentes.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-transparent dark:border-amber-800">
                  <div className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900 dark:text-amber-300">
                        {ausentes.length} variável(is) não encontrada(s) no quarto
                      </p>
                      <p className="text-amber-700 dark:text-amber-400">
                        Crie as variáveis no quarto ou corrija o nome na funcionalidade.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">Funcionalidades vinculadas</h4>
                {funcionalidadesTeste.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma funcionalidade vinculada a esta conexão.</p>
                ) : (
                  funcionalidadesTeste.map((func) => (
                    <div
                      key={func.codigo}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        func.variavel_existe ? "border-border" : "border-amber-300 dark:border-amber-700"
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{func.codigo}</p>
                        <p className="text-xs text-muted-foreground font-mono">{func.nome_variavel}</p>
                      </div>
                      <Badge
                        className={
                          func.variavel_existe
                            ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                            : "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700"
                        }
                      >
                        {func.variavel_existe ? (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 mr-1" />
                        )}
                        {func.variavel_existe ? "Variável existe" : "Variável não existe"}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-transparent dark:border-red-800">
              <div className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900 dark:text-red-300">Falha ao testar a conexão</p>
                  <p className="text-red-700 dark:text-red-400 break-words">{resultado.erro}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
